import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileText, LayoutList, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";
import { Loading } from "@/components/common";
import { Action, EmptyState, PageHeader, Panel, PanelHeader, SearchField, StatusBadge } from "./ui";
import { FlagChips, PatientAvatar, PriorityNote, dateTimeBR, dayBR } from "./MeetingBits";
import { AllocationPanel } from "./MeetingAllocation";
import { AgeChip, defaultWardFilter, wardLabel, wardScope } from "@/lib/patient";
import { DiscussedBlocks } from "./MinutesDiscussed";
import { EMPTY_MEETING_FILTERS, readMeetingFilters, writeMeetingFilters } from "./meetingFilters";
import { useScrollIntoViewWhen } from "@/lib/scrollTo";

const EMPTY_FILTERS = EMPTY_MEETING_FILTERS;

const selectClass =
  "h-11 w-full rounded-xl border border-border bg-white px-3.5 text-[14px] text-ink-800 focus:border-sage-600 focus:outline-none focus:ring-2 focus:ring-sage-600/15";

const QueueRow = ({ item, onOpen }) => (
  <li
    data-testid={`meeting-queue-item-${item.patient_id}`}
    className={`flex flex-wrap items-start justify-between gap-4 rounded-xl border px-4 py-3.5 ${
      item.priority ? "border-danger/45 bg-danger/[0.05]" : "border-border bg-white"
    }`}
  >
    <div className="flex min-w-[260px] flex-1 gap-3.5">
      <PatientAvatar
        name={item.full_name}
        initials={item.initials}
        photoUrl={item.photo_thumb_url}
        testId={`meeting-queue-avatar-${item.patient_id}`}
      />
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[15.5px] font-semibold text-ink-900 break-words">{item.full_name}</p>
          <AgeChip patient={item} testId={`meeting-queue-age-${item.patient_id}`} />
          <span data-testid={`meeting-queue-ward-${item.patient_id}`} className="text-[13px] text-ink-500">
            Ala: {wardLabel(item)}
          </span>
        </div>
        <PriorityNote item={item} testId={`meeting-queue-priority-${item.patient_id}`} />
        <p className="text-[13.5px] text-ink-600">
          {item.admission_days ?? "—"} dia(s) de internação · Última discussão:{" "}
          {item.last_discussion_at ? dateTimeBR(item.last_discussion_at) : "nunca"} ·{" "}
          {item.never_discussed ? "sem discussão" : `${item.days_without_discussion} dia(s) sem discussão`}
        </p>
        <p className="text-[13.5px] text-ink-600">Alta prevista: {dayBR(item.expected_discharge_date)}</p>
        <p className="text-[13.5px] text-ink-600">
          Conselheiro: {item.care_team?.counselor?.name || "não definido"} · Psicólogo(a):{" "}
          {item.care_team?.psychologist?.name || "não definido"} · Psiquiatra:{" "}
          {item.care_team?.psychiatrist?.name || "não definido"}
        </p>
        <FlagChips flags={item.flags} testId={`meeting-queue-flags-${item.patient_id}`} />
      </div>
    </div>
    <div className="flex flex-col items-end gap-2">
      <StatusBadge
        tone={item.meeting_status === "discussed" ? "done" : "pending"}
        testId={`meeting-queue-status-${item.patient_id}`}
      >
        {item.meeting_status === "discussed" ? "Discutido" : "Pendente"}
      </StatusBadge>
      <Action
        variant="secondary"
        size="sm"
        icon={ArrowRight}
        data-testid={`meeting-queue-open-${item.patient_id}`}
        onClick={onOpen}
      >
        {item.meeting_status === "discussed" ? "Complementar" : "Discutir"}
      </Action>
    </div>
  </li>
);

const ReviewPanel = ({ review, onClose, closing, onCancel }) => {
  const [discard, setDiscard] = useState(false);
  return (
    <Panel testId="meeting-review-panel" className="space-y-5">
      <PanelHeader
        label="Encerramento"
        title="Revisar e encerrar reunião"
        hint="Confira o que foi registrado. A reunião pode ser encerrada mesmo com pacientes pendentes."
      />
      <dl className="grid gap-3 sm:grid-cols-2">
        <div className="rounded-xl bg-sand/60 px-3.5 py-3">
          <dt className="er-label text-[12px]">Início</dt>
          <dd data-testid="review-started-at" className="mt-1 text-[15px] font-semibold text-ink-800">
            {dateTimeBR(review.meeting?.opened_at)}
          </dd>
        </div>
        <div className="rounded-xl bg-sand/60 px-3.5 py-3">
          <dt className="er-label text-[12px]">Participantes</dt>
          <dd data-testid="review-participants" className="mt-1 text-[14px] text-ink-800">
            {(review.meeting?.participants || []).map((p) => p.name).join(", ") || "—"}
          </dd>
        </div>
      </dl>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="er-label text-[12px]">Pacientes discutidos ({review.discussed.length})</p>
          <ul data-testid="review-discussed" className="mt-2 space-y-1 text-[14px] text-ink-700">
            {review.discussed.map((d) => (
              <li key={d.patient_id}>{d.patient_name}</li>
            ))}
            {review.discussed.length === 0 ? <li className="text-ink-500">Nenhum</li> : null}
          </ul>
        </div>
        <div>
          <p className="er-label text-[12px]">Pacientes pendentes ({review.pending.length})</p>
          <ul data-testid="review-pending" className="mt-2 max-h-40 space-y-1 overflow-y-auto text-[14px] text-ink-700">
            {review.pending.map((d) => (
              <li key={d.patient_id}>{d.full_name}</li>
            ))}
            {review.pending.length === 0 ? <li className="text-ink-500">Nenhum</li> : null}
          </ul>
        </div>
      </div>

      {(review.minutes_preview?.discussed || []).length ? (
        <div className="space-y-3">
          <p className="er-label text-[12px]">Prévia da ata — registros desta reunião</p>
          <DiscussedBlocks items={review.minutes_preview.discussed} testId="review-minutes-preview" />
        </div>
      ) : null}

      {review.pending_priority.length ? (
        <div
          data-testid="review-priority-alert"
          className="rounded-xl border border-danger/45 bg-danger/[0.06] px-4 py-3.5 text-[14px] text-ink-800"
        >
          <p className="font-semibold text-danger">
            Atenção: {review.pending_priority.length} paciente(s) prioritário(s) seguem sem discussão
          </p>
          <ul className="mt-2 max-h-56 space-y-1 overflow-y-auto">
            {review.pending_priority.slice(0, 50).map((p) => (
              <li key={p.patient_id}>
                {p.full_name} —{" "}
                {p.never_discussed ? "nunca discutido" : `há ${p.days_without_discussion} dias sem discussão`}
              </li>
            ))}
            {review.pending_priority.length > 50 ? (
              <li className="font-semibold">
                … e outros {review.pending_priority.length - 50} paciente(s) prioritário(s)
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}

      {review.incomplete.length ? (
        <div data-testid="review-incomplete" className="space-y-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3.5">
          <p className="text-[14px] font-semibold text-amber-900">
            Discussões iniciadas e não concluídas ({review.incomplete.length})
          </p>
          <ul className="space-y-1 text-[14px] text-ink-800">
            {review.incomplete.map((d) => (
              <li key={d.patient_id}>{d.patient_name}</li>
            ))}
          </ul>
          <label className="flex items-start gap-2 text-[14px] text-ink-800">
            <input
              type="checkbox"
              data-testid="review-discard-checkbox"
              checked={discard}
              onChange={(e) => setDiscard(e.target.checked)}
              className="mt-1"
            />
            Confirmo o descarte desses rascunhos ao encerrar a reunião.
          </label>
        </div>
      ) : null}

      {review.flag_changes.length ? (
        <div>
          <p className="er-label text-[12px]">Marcações alteradas</p>
          <ul data-testid="review-flags" className="mt-2 space-y-1 text-[14px] text-ink-700">
            {review.flag_changes.map((c, i) => (
              <li key={`${c.flag}-${i}`}>
                {c.patient_name}: {c.label} — {c.action === "open" ? "aberta" : "encerrada"}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {review.reallocations.length ? (
        <div>
          <p className="er-label text-[12px]">Realocações</p>
          <ul data-testid="review-reallocations" className="mt-2 space-y-1 text-[14px] text-ink-700">
            {review.reallocations.map((r, i) => (
              <li key={`${r.role}-${i}`}>
                {r.patient_name}: {r.role_label} — {r.previous_name || "não definido"} → {r.new_name || "não definido"}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="flex flex-wrap gap-2.5">
        <Action
          icon={Check}
          data-testid="meeting-close-confirm"
          disabled={closing || (review.incomplete.length > 0 && !discard)}
          onClick={() => onClose(discard)}
        >
          {closing ? "Encerrando…" : "Confirmar encerramento"}
        </Action>
        <Action variant="secondary" data-testid="meeting-close-cancel" onClick={onCancel}>
          Voltar para a fila
        </Action>
      </div>
    </Panel>
  );
};

export default function MeetingRoom() {
  const { meetingId } = useParams();
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [queue, setQueue] = useState(null);
  const [filters, setFilters] = useState(() => {
    const stored = readMeetingFilters(meetingId);
    return stored.ward === EMPTY_MEETING_FILTERS.ward ? { ...stored, ward: defaultWardFilter(user) } : stored;
  });
  const [options, setOptions] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [showAllocation, setShowAllocation] = useState(false);
  const [review, setReview] = useState(null);
  const [closing, setClosing] = useState(false);
  const reviewRef = useRef(null);
  useScrollIntoViewWhen(reviewRef, Boolean(review));

  const loadQueue = useCallback(async () => {
    try {
      const { data } = await api.get(`/portal/meetings/${meetingId}/queue`, {
        params: {
          q: filters.q || undefined,
          counselor: filters.counselor || undefined,
          psychologist: filters.psychologist || undefined,
          psychiatrist: filters.psychiatrist || undefined,
          ward: filters.ward || "all",
        },
      });
      setQueue(data.items);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setQueue([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId, filters]);

  useEffect(() => {
    writeMeetingFilters(meetingId, filters);
  }, [meetingId, filters]);

  useEffect(() => {
    api
      .get(`/portal/meetings/${meetingId}`)
      .then(({ data }) => setMeeting(data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    api
      .get("/portal/meetings/filter-options")
      .then(({ data }) => setOptions(data))
      .catch(() => setOptions(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetingId]);

  useEffect(() => {
    const timer = setTimeout(loadQueue, 250);
    return () => clearTimeout(timer);
  }, [loadQueue]);

  const openAllocation = async () => {
    try {
      const { data } = await api.get("/portal/meetings/allocation", {
        params: { ward: filters.ward || "all" },
      });
      setAllocation(data);
      setShowAllocation(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const openReview = async () => {
    try {
      const { data } = await api.get(`/portal/meetings/${meetingId}/review`);
      setReview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const closeMeeting = async (discard) => {
    setClosing(true);
    try {
      await api.post(`/portal/meetings/${meetingId}/close`, { discard_incomplete: discard });
      toast.success("Reunião encerrada e ata gerada.");
      navigate(`/portal/reunioes/${meetingId}/ata`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setClosing(false);
  };

  const counts = useMemo(() => {
    const items = queue || [];
    return {
      total: items.length,
      pending: items.filter((i) => i.meeting_status === "pending").length,
      priority: items.filter((i) => i.priority && i.meeting_status === "pending").length,
    };
  }, [queue]);

  if (!meeting) return <Loading label="Carregando reunião…" />;
  const isOpen = meeting.status === "open";

  return (
    <div data-testid="meeting-room-page" className="space-y-6">
      <button
        type="button"
        data-testid="meeting-back-to-list"
        onClick={() => navigate("/portal/reunioes")}
        className="inline-flex items-center gap-2 text-[14px] font-semibold text-ink-600 hover:text-sage-700"
      >
        <ArrowLeft size={16} aria-hidden="true" /> Reuniões Clínicas
      </button>

      <PageHeader
        kicker={isOpen ? "Reunião em andamento" : "Reunião encerrada"}
        title="Fila de pacientes"
        subtitle={`Aberta em ${dateTimeBR(meeting.opened_at)} por ${meeting.opened_by_name || "—"} · Participantes: ${
          (meeting.participants || []).map((p) => p.name).join(", ") || "—"
        }`}
        actions={
          <>
            <Action variant="secondary" icon={Users} data-testid="meeting-allocation-button" onClick={openAllocation}>
              Ver alocação da equipe
            </Action>
            {isOpen ? (
              <Action icon={FileText} data-testid="meeting-review-button" onClick={openReview}>
                Revisar e encerrar reunião
              </Action>
            ) : (
              <Action
                icon={FileText}
                data-testid="meeting-minutes-button"
                onClick={() => navigate(`/portal/reunioes/${meetingId}/ata`)}
              >
                Ver ata
              </Action>
            )}
          </>
        }
      />

      {showAllocation && allocation ? (
        <AllocationPanel data={allocation} onClose={() => setShowAllocation(false)} backLabel="Ocultar" />
      ) : null}

      {review ? (
        <div ref={reviewRef} className="scroll-mt-32">
          <ReviewPanel review={review} onClose={closeMeeting} closing={closing} onCancel={() => setReview(null)} />
        </div>
      ) : null}

      <Panel testId="meeting-filters" className="space-y-4">
        <PanelHeader
          label="Filtros"
          title="Refinar a fila"
          hint="Os filtros funcionam juntos e não alteram a ordem de prioridade."
        />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          <SearchField
            testId="meeting-filter-name"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Buscar paciente pelo nome"
          />
          {wardScope(user).locked ? (
            <span
              data-testid="meeting-ward-scope"
              className="inline-flex h-10 items-center rounded-xl bg-sand px-3.5 text-[14px] font-semibold text-ink-700"
            >
              {wardScope(user).label}
            </span>
          ) : (
          <select
            data-testid="meeting-filter-ward"
            aria-label="Ala"
            className={selectClass}
            value={filters.ward}
            onChange={(e) => setFilters({ ...filters, ward: e.target.value })}
          >
            {(options?.ward?.items || [{ value: "all", label: "Todas as alas" }]).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          )}
          {["counselor", "psychologist", "psychiatrist"].map((key) => (
            <select
              key={key}
              data-testid={`meeting-filter-${key}`}
              className={selectClass}
              value={filters[key]}
              onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
            >
              <option value="">Todos os {options?.[key]?.label?.toLowerCase() || key}s</option>
              <option value="none">Sem profissional definido</option>
              {(options?.[key]?.items || []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          ))}
          <Action variant="secondary" data-testid="meeting-filters-clear" onClick={() => setFilters({ ...EMPTY_FILTERS, ward: defaultWardFilter(user) })}>
            Limpar filtros
          </Action>
        </div>
        <p data-testid="meeting-queue-counts" className="text-[13.5px] text-ink-600">
          {counts.total} paciente(s) no resultado · {counts.pending} pendente(s) · {counts.priority} prioritário(s)
          pendente(s)
          {filters.ward && filters.ward !== "all"
            ? ` · Ala: ${(options?.ward?.items || []).find((o) => o.value === filters.ward)?.label || filters.ward}`
            : ""}
        </p>
      </Panel>

      {queue === null ? (
        <Loading label="Carregando fila…" />
      ) : queue.length === 0 ? (
        <EmptyState
          testId="meeting-queue-empty"
          icon={LayoutList}
          title="Nenhum paciente no resultado"
          description="Ajuste a busca ou limpe os filtros para ver a fila completa."
        />
      ) : (
        <ul data-testid="meeting-queue-list" className="space-y-3">
          {queue.map((item) => (
            <QueueRow
              key={item.patient_id}
              item={item}
              onOpen={() => navigate(`/portal/reunioes/${meetingId}/paciente/${item.patient_id}`)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
